// composables/useSupportForm.ts
import { ref } from 'vue'
import axios from 'axios'
import { useAidaSessionStore } from '../../stores/aidaSessionStore'
import { useUserProfileStore } from '../../stores/userProfileStore'
import { v4 as uuidv4 } from 'uuid'

interface SupportFormData {
  question_number: string
  user_session_id: string
  email: string
  first_name: string
  last_name: string
  phone_number: string
  subject: string
  description: string
  priority: string
  subscription_id: string
  product: string
  version: string
  error_message: string
  recaptcha_token: string
  history: string
  files?: File[]
}

export function useSupportForm() {
  const loading = ref(false)
  const error = ref<string | null>(null)
  const success = ref(false)
  const BACKEND_BASE_URL = import.meta.env.VITE_BACKEND_BASE_URL

  const aidaSessionStore = useAidaSessionStore()
  const credentialsBase64 = aidaSessionStore.authBase64()

  const userProfileStore = useUserProfileStore()

  const submitSupportForm = async (formData: SupportFormData) => {
    loading.value = true
    error.value = null
    success.value = false

    const sessionId =
      formData.user_session_id && formData.user_session_id.trim() !== ''
        ? formData.user_session_id
        : uuidv4()

    try {
      const data = new FormData()
      data.append('users_question_order_number', formData.question_number)
      data.append('users_session_id', sessionId)
      data.append('email', formData.email)
      data.append('first_name', formData.first_name)
      data.append('last_name', formData.last_name)
      data.append('phone_number', formData.phone_number)
      data.append('subject', formData.subject)
      data.append('description', formData.description)
      data.append('priority', formData.priority)
      data.append('subscription_id', formData.subscription_id)
      data.append('product', formData.product)
      data.append('version', formData.version)
      data.append('error_message', formData.error_message)
      data.append('recaptcha_token', formData.recaptcha_token)
      data.append('chat_history', formData.history)

      formData.files?.forEach(file => {
        data.append('files', file)
      })

      const response = await axios.post(BACKEND_BASE_URL + '/support-requests', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `${credentialsBase64}`,
          'Authorization-Token': `Bearer ${userProfileStore.profile?.idToken}`,
        },
      })

      success.value = true
      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    error,
    success,
    submitSupportForm,
  }
}
